import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { AIAgent } from '../../types';
import favoriteAgentService from '../../services/api/favoriteAgentService';
import aiAgentService, { CreateAgentRequest, UpdateAgentRequest } from '../../services/api/aiAgentService';

// AIエージェント状態の型定義
interface AIAgentState {
  agents: AIAgent[];
  selectedAgentId: number | null;
  isLoading: boolean;
  error: string | null;
}

// 初期状態
const initialState: AIAgentState = {
  agents: [],
  selectedAgentId: null,
  isLoading: false,
  error: null,
};

// AIエージェント一覧取得アクション
export const fetchAIAgents = createAsyncThunk(
  'aiAgent/fetchAIAgents',
  async (includeAll: boolean = false, { rejectWithValue }) => {
    try {
      // tokenManagerを使用してトークンを取得（スーパーリロード対応）
      const tokenManager = await import('../../utils/tokenManager').then(
        module => module.default
      );
      const token = tokenManager.getToken();

      if (!token) {
        console.warn('fetchAIAgents: 認証トークンがありません');
        return rejectWithValue('認証トークンがありません');
      }

      // aiAgentService を使用して一貫したAPIアクセスを行う
      const aiAgentService = await import('../../services/api/aiAgentService').then(
        module => module.default
      );
      const agents = await aiAgentService.getActiveAgents(includeAll);
      
      if (!agents || agents.length === 0) {
        console.warn('AIエージェントが取得できなかったか、空の配列が返されました');
      }
      
      return agents;
    } catch (error) {
      console.error('fetchAIAgents エラー:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'サーバーとの通信に失敗しました');
    }
  }
);

// 注: お気に入りエージェントは active API で取得されるため、
// この専用関数は削除されました

// エージェントのお気に入り状態をトグルするアクション
export const toggleFavoriteAgent = createAsyncThunk(
  'aiAgent/toggleFavoriteAgent',
  async (agentId: number, { getState, rejectWithValue, dispatch }) => {
    try {
      // tokenManagerを使用してトークンを取得（スーパーリロード対応）
      const tokenManager = await import('../../utils/tokenManager').then(
        module => module.default
      );
      const token = tokenManager.getToken();

      if (!token) {
        console.log('認証されていないため、お気に入り操作はスキップします');
        return rejectWithValue('認証が必要です。ログインしてください。');
      }

      // API呼び出し
      const result = await favoriteAgentService.toggleFavorite(agentId);
      
      // 現在のエージェントの状態を取得
      const rootState = getState() as RootState;
      const currentAgent = rootState.aiAgent.agents.find(agent => agent.id === agentId);
      const currentFavoritedStatus = currentAgent?.is_favorited || false;
      
      // APIレスポンスから新しいステータスを取得
      // 注: データ形式が { data: { agent_id: number, is_favorited: boolean } } または同等のもの
      const responseData = result.data;
      
      if (responseData && typeof responseData === 'object') {
        // 直接レスポンスデータから取得
        const isFavorited = responseData.is_favorited !== undefined 
          ? responseData.is_favorited 
          : !currentFavoritedStatus;  // フォールバック: 現在の状態を反転
        
        console.log('トグル結果:', { agentId, newStatus: isFavorited });
        
        return { agentId, isFavorited };
      } else {
        // データが期待した形式でない場合、現在の状態を反転して返す
        return { agentId, isFavorited: !currentFavoritedStatus };
      }
    } catch (error) {
      console.error(`toggleFavoriteAgent(${agentId}) エラー:`, error);
      return rejectWithValue(error instanceof Error ? error.message : 'お気に入り状態の更新に失敗しました');
    }
  }
);

// AIエージェント詳細取得アクション
export const fetchAIAgentById = createAsyncThunk(
  'aiAgent/fetchAIAgentById',
  async (agentId: number, { rejectWithValue }) => {
    try {
      // tokenManagerを使用してトークンを取得（スーパーリロード対応）
      const tokenManager = await import('../../utils/tokenManager').then(
        module => module.default
      );
      const token = tokenManager.getToken();

      if (!token) {
        return rejectWithValue('認証トークンがありません');
      }

      // aiAgentService を使用してAPI呼び出しを行う
      const aiAgentService = await import('../../services/api/aiAgentService').then(
        module => module.default
      );
      const agent = await aiAgentService.getAgentById(agentId);

      if (!agent) {
        return rejectWithValue('AIエージェントが見つかりませんでした');
      }

      return agent;
    } catch (error) {
      console.error(`fetchAIAgentById(${agentId}) エラー:`, error);
      return rejectWithValue(error instanceof Error ? error.message : 'AIエージェントの取得に失敗しました');
    }
  }
);

// ユーザーエージェント作成アクション
export const createUserAgent = createAsyncThunk(
  'aiAgent/createUserAgent',
  async (agentData: CreateAgentRequest, { rejectWithValue }) => {
    try {
      // tokenManagerを使用してトークンを取得
      const tokenManager = await import('../../utils/tokenManager').then(
        module => module.default
      );
      const token = tokenManager.getToken();

      if (!token) {
        return rejectWithValue('認証トークンがありません');
      }

      // エージェントを作成
      const agent = await aiAgentService.createAgent(agentData);

      console.log('エージェント作成成功:', agent);
      return agent;
    } catch (error: any) {
      console.error('createUserAgent エラー:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'エージェントの作成に失敗しました';
      return rejectWithValue(errorMessage);
    }
  }
);

// ユーザーエージェント更新アクション
export const updateUserAgent = createAsyncThunk(
  'aiAgent/updateUserAgent',
  async ({ agentId, agentData }: { agentId: number; agentData: UpdateAgentRequest }, { rejectWithValue }) => {
    try {
      // tokenManagerを使用してトークンを取得
      const tokenManager = await import('../../utils/tokenManager').then(
        module => module.default
      );
      const token = tokenManager.getToken();

      if (!token) {
        return rejectWithValue('認証トークンがありません');
      }

      // エージェントを更新
      const agent = await aiAgentService.updateAgent(agentId, agentData);

      console.log('エージェント更新成功:', agent);
      return agent;
    } catch (error: any) {
      console.error(`updateUserAgent(${agentId}) エラー:`, error);
      const errorMessage = error?.response?.data?.message || error?.message || 'エージェントの更新に失敗しました';
      return rejectWithValue(errorMessage);
    }
  }
);

// ユーザーエージェント削除アクション
export const deleteUserAgent = createAsyncThunk(
  'aiAgent/deleteUserAgent',
  async (agentId: number, { rejectWithValue }) => {
    try {
      // tokenManagerを使用してトークンを取得
      const tokenManager = await import('../../utils/tokenManager').then(
        module => module.default
      );
      const token = tokenManager.getToken();

      if (!token) {
        return rejectWithValue('認証トークンがありません');
      }

      // エージェントを削除
      await aiAgentService.deleteAgent(agentId);

      console.log('エージェント削除成功:', agentId);
      return agentId;
    } catch (error: any) {
      console.error(`deleteUserAgent(${agentId}) エラー:`, error);
      const errorMessage = error?.response?.data?.message || error?.message || 'エージェントの削除に失敗しました';
      return rejectWithValue(errorMessage);
    }
  }
);

// AIエージェントスライス
const aiAgentSlice = createSlice({
  name: 'aiAgent',
  initialState,
  reducers: {
    setSelectedAgent: (state, action: PayloadAction<number | null>) => {
      state.selectedAgentId = action.payload;
    },
    clearAIAgentError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // AIエージェント一覧取得
    builder
      .addCase(fetchAIAgents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAIAgents.fulfilled, (state, action: PayloadAction<AIAgent[]>) => {
        state.isLoading = false;
        state.agents = action.payload;
        
        // 選択中のエージェントが存在しないとき、最初のエージェントを選択する
        if (state.selectedAgentId === null && action.payload.length > 0) {
          state.selectedAgentId = action.payload[0].id;
        }
        
        state.error = null;
      })
      .addCase(fetchAIAgents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // AIエージェント詳細取得
    builder
      .addCase(fetchAIAgentById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAIAgentById.fulfilled, (state, action: PayloadAction<AIAgent>) => {
        state.isLoading = false;
        
        // エージェント一覧の更新
        const index = state.agents.findIndex(agent => agent.id === action.payload.id);
        if (index !== -1) {
          state.agents[index] = action.payload;
        } else {
          state.agents.push(action.payload);
        }
        
        // 選択中のエージェントを設定
        state.selectedAgentId = action.payload.id;
        
        state.error = null;
      })
      .addCase(fetchAIAgentById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
      
    // 注: お気に入りエージェント一覧処理は削除されました
      
    // お気に入りトグル
    builder
      .addCase(toggleFavoriteAgent.pending, (state) => {
        state.error = null;
      })
      .addCase(toggleFavoriteAgent.fulfilled, (state, action: PayloadAction<{ agentId: number, isFavorited: boolean }>) => {
        // エージェントのお気に入り状態を更新
        const { agentId, isFavorited } = action.payload;
        const agentIndex = state.agents.findIndex(agent => agent.id === agentId);

        if (agentIndex !== -1) {
          // @ts-ignore - is_favorited属性を動的に付与
          state.agents[agentIndex].is_favorited = isFavorited;
        }

        state.error = null;
      })
      .addCase(toggleFavoriteAgent.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // ユーザーエージェント作成
    builder
      .addCase(createUserAgent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUserAgent.fulfilled, (state, action: PayloadAction<AIAgent>) => {
        state.isLoading = false;
        // 新しく作成されたエージェントを配列の先頭に追加
        state.agents.unshift(action.payload);
        state.error = null;
      })
      .addCase(createUserAgent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // ユーザーエージェント更新
    builder
      .addCase(updateUserAgent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserAgent.fulfilled, (state, action: PayloadAction<AIAgent>) => {
        state.isLoading = false;
        // 更新されたエージェントで配列内の該当エージェントを置き換え
        const index = state.agents.findIndex(agent => agent.id === action.payload.id);
        if (index !== -1) {
          state.agents[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateUserAgent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // ユーザーエージェント削除
    builder
      .addCase(deleteUserAgent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUserAgent.fulfilled, (state, action: PayloadAction<number>) => {
        state.isLoading = false;
        // 削除されたエージェントを配列から削除
        state.agents = state.agents.filter(agent => agent.id !== action.payload);

        // 削除されたエージェントが選択されていた場合、選択をクリア
        if (state.selectedAgentId === action.payload) {
          state.selectedAgentId = state.agents.length > 0 ? state.agents[0].id : null;
        }

        state.error = null;
      })
      .addCase(deleteUserAgent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedAgent, clearAIAgentError } = aiAgentSlice.actions;

export default aiAgentSlice.reducer;
